import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { useBillingStyles } from "./styles";
import CloseIcon from "@material-ui/icons/Close";
import MastercardIcon from "../../../../assets/svgs/MastercardIcon";
import {
  deleteToken,
  getAllTokens,
  updateToken,
} from "../../../../api/ccbillApi";
import { showToast } from "../../../../utils/toast";
import MastercardVisaIcon from "../../../../assets/svgs/MastercardVisoIcon";

interface PaymentToken {
  id: string;
  last4: string;
  expDate: string;
  cardType: string;
  isEnabled: boolean;
  createdAt: string;
  userId?: string;
}

interface BillingProps {
  handleSave: () => void;
}

const Billing: React.FC<BillingProps> = ({ handleSave }) => {
  const classes = useBillingStyles();
  const [paymentTokens, setPaymentTokens] = useState<PaymentToken[]>([]);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deletingTokenId, setDeletingTokenId] = useState<string | null>(null);

  useEffect(() => {
    const fetchTokens = async () => {
      setLoading(true);
      try {
        const tokens = await getAllTokens();
        setPaymentTokens(tokens.cards);
        if (tokens.cards.length > 0) {
          const enabledToken = tokens.cards.find(
            (token: PaymentToken) => token.isEnabled
          );
          setSelectedCard(enabledToken ? enabledToken.id : tokens[0].id);
        }
      } catch (err) {
        console.error("❌ Error fetching payment tokens:", err);
        showToast("Payment methods could not be downloaded", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchTokens();
  }, []);

  const handleCardSelect = async (cardId: string) => {
    if (cardId === selectedCard) return;

    try {
      if (selectedCard) {
        await updateToken(selectedCard, false);
      }
      await updateToken(cardId, true);
      setSelectedCard(cardId);
      setPaymentTokens(prev =>
        prev.map(token =>
          token.id === cardId
            ? { ...token, isEnabled: true }
            : { ...token, isEnabled: false }
        )
      );
      showToast("payment method updated", "success");
    } catch (err) {
      console.error("❌ Error updating token:", err);
      showToast("Failed to update payment method", "error");
    }
  };

  const handleDeleteToken = async (tokenId: string) => {
    setDeletingTokenId(tokenId);
    try {
      await deleteToken(tokenId);
      setPaymentTokens(prev => prev.filter(token => token.id !== tokenId));
      if (selectedCard === tokenId) {
        const remainingTokens = paymentTokens.filter(
          token => token.id !== tokenId
        );
        if (remainingTokens.length > 0) {
          setSelectedCard(remainingTokens[0].id);
          await updateToken(remainingTokens[0].id, true);
        } else {
          setSelectedCard(null);
        }
      }
      showToast("Payment method deleted", "success");
    } catch (err) {
      console.error("❌ Error deleting token:", err);
      showToast("Failed to remove payment method", "error");
    } finally {
      setDeletingTokenId(null);
    }
  };

  return (
    <div className={classes.section}>
      <Typography className={classes.sectionTitle}>Payment methods</Typography>
      <Typography className={classes.infoTextTitle}>
        Secure payment methods for your account and content delivery.
      </Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress size={24} style={{ color: "#FFFFFF" }} />
        </Box>
      ) : paymentTokens.length === 0 ? (
        <Typography style={{ color: "#FFFFFF", marginTop: "16px" }}>
          No payment methods yet. Your card will be saved automatically after
          your first successful purchase.
        </Typography>
      ) : (
        paymentTokens.map(token => (
          <Box className={classes.paymentMethodItem} key={token.id}>
            <Box
              className={`${classes.radioCircle} ${
                selectedCard === token.id ? classes.selected : ""
              }`}
              onClick={() => handleCardSelect(token.id)}
            />
            {token.cardType.toLowerCase() === "visa" ? (
              <MastercardVisaIcon />
            ) : (
              <MastercardIcon />
            )}
            <Box className={classes.cardDetails}>
              <Typography className={classes.cardName}>
                {token.cardType} **** {token.last4}
              </Typography>
              <Typography className={classes.cardExpiry}>
                {token.expDate}
              </Typography>
            </Box>
            <CloseIcon
              className={classes.deleteIcon}
              onClick={() => handleDeleteToken(token.id)}
              style={{
                cursor:
                  deletingTokenId === token.id ? "not-allowed" : "pointer",
              }}
            />
            {deletingTokenId === token.id && (
              <CircularProgress
                size={16}
                style={{
                  color: "#FFFFFF",
                  position: "absolute",
                  right: "10px",
                }}
              />
            )}
          </Box>
        ))
      )}
      <Typography style={{ color: "#A3A3A3", marginTop: "16px" }}>
        Manage saved cards here. To add a new card, complete a purchase and it
        will be stored for future one-click payments.
      </Typography>
    </div>
  );
};

export default Billing;
